import styled from '@emotion/styled';
import { Color } from '../../../../styles/Color';
import { RouterLink } from '../../link/RouterLink';
import { dropdown } from '../../../../styles/animation/dropdown';
import { IMenuItemEntry } from '../../../../contentful/types/IMenuItem';
import { useCommonData } from '../../../../common-data/useCommonData';
import { getMenuItemMapping } from '../../../../utils/getMenuItemMapping';
import { IMenuItemNavBar } from '../../../../contentful/types/IMenuItemNavBar';
import {
	Css,
	ITheme,
	Responsive,
	UnstyledLi,
	UnstyledUl,
} from '../../../../ui';
import { Shadow } from '../../../../styles/Shadow';
import { Z_INDEX_NAVIGATION_MENU } from '../../../../styles/zIndex';

interface ISubNavigation {
	subMenuItems: IMenuItemEntry[];
	isOpen: boolean;
	withThemeColors?: boolean;
}

export default function SubNavigation({
	subMenuItems,
	isOpen,
	withThemeColors,
}: ISubNavigation) {
	const { pathToPageIdMap } = useCommonData();

	const menuItemNav = getMenuItemMapping(pathToPageIdMap, subMenuItems);

	if (!menuItemNav) {
		return null;
	}

	return (
		<StyledSubmenu isOpen={isOpen}>
			{menuItemNav.map((item: IMenuItemNavBar) => (
				<StyledSubmenuItemWrap key={item.id} className="no-max-width">
					<StyledSubmenuItem href={item.url} withThemeColors={withThemeColors}>
						{item.title}
					</StyledSubmenuItem>
				</StyledSubmenuItemWrap>
			))}
		</StyledSubmenu>
	);
}

const StyledSubmenu = styled(UnstyledUl)<{ isOpen: boolean }>`
	background-color: ${Color.Neutral1};
	width: ${Css.pxToRem(300)};
	height: fit-content;
	border-radius: 0 ${Css.pxToRem(8)} ${Css.pxToRem(8)};
	position: absolute;
	top: ${Css.pxToRem(58)};
	padding: ${Css.pxToRem(24)} ${Css.pxToRem(16)};
	display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
	flex-direction: column;
	gap: ${Css.pxToRem(24)};
	font-weight: 500;
	${({ isOpen }) => dropdown(isOpen)}
	box-shadow: ${Shadow.Dropdown};
	z-index: ${Z_INDEX_NAVIGATION_MENU};

	li {
		${({ isOpen }) => !isOpen && 'display: none;'};
	}
`;

export const StyledSubmenuItemWrap = styled(UnstyledLi)`
	position: relative;
`;

export const StyledSubmenuItem = styled(RouterLink)<
	{ withThemeColors?: boolean },
	ITheme
>`
	text-decoration: none;
	color: ${Color.OnBackground};
	padding: ${Css.pxToRem(8)};
	position: relative;

	&:hover {
		color: ${Color.OnBackground};
		text-decoration: underline;
		text-underline-offset: ${Css.pxToRem(6)};
		text-decoration-color: ${(props) =>
			props.withThemeColors ? props.theme.color.brand : Color.OnBackground};
		text-decoration-thickness: 2px;
	}

	:focus-visible {
		outline: 2px solid
			${(props) =>
				props.withThemeColors ? props.theme.color.brand : Color.OnBackground};
		border-radius: ${(props) => props.theme.button.radius};
		background-color: ${(props) =>
			props.withThemeColors ? props.theme.color.primaryLight : Color.Neutral3};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-weight: 300;
		font-size: ${Css.pxToRem(14)};
		padding: 0 0 0 ${Css.pxToRem(16)};

		&:hover {
			text-decoration: none;
			color: ${(props) =>
				props.withThemeColors ? props.theme.color.brand : Color.OnBackground};
		}
	}
`;
